import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CssBaseline,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ShieldMoonRoundedIcon from '@mui/icons-material/ShieldMoonRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import AppNavbar from '../../components/AppNavbar';
import Header from '../../components/Header';
import SideMenu from '../../components/SideMenu';
import ThreadCardsView from '../../components/workspace/cards/ThreadCardsView';
import AppTheme from '../../shared-theme/AppTheme';
import { formatDateTime, getPartnerDisplayName } from '../../components/workspace/messages/messageUtils';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const PRODUCTION_CORR_ID = 'demo-amx-hybrid-build-001';
const EXPERT_CORR_ID = 'demo-amx-expert-support-001';

const demoWorkspace = {
  workspace_id: 'amx-demo-workspace',
  title: 'AMX Demo Workspace',
  description: 'Interactive AMX admin demo showing request routing, capability selection, digital thread telemetry, expert support, and part visualization.',
  status: 'active',
  is_cui: false,
  created_at: '2026-06-01T13:40:00Z',
  updated_at: '2026-06-01T15:18:00Z',
  created_by_partner_id: 'amx-demo',
  created_by_partner: {
    partner_id: 'amx-demo',
    partner_code: 'AMX-DEMO',
    display_name: 'AMX Demo Program Office',
    legal_name: 'Advanced Manufacturing Exchange',
    contact_email: 'demo@amx.example',
  },
};

const amxNetworkPartner = {
  partner_id: 'amx-network',
  partner_code: 'AMX-NET',
  display_name: 'AMX Advanced Manufacturing Network',
  legal_name: 'Advanced Manufacturing Exchange',
  contact_email: 'network@amx.example',
  status: 'active',
};

const meltioAsset = {
  message_uuid: 'demo-asset-haas-meltio',
  title: 'HAAS UMC-750 + Meltio Engine Cell',
  body: {
    asset_name: 'HAAS UMC-750 + Meltio Engine Cell',
    asset_source_id: 'asset-haas-meltio-01',
    capability_source_id: 'cap-hybrid-ded-cnc',
    machine_builder: 'HAAS / Meltio',
    controller: 'Haas NGC + Meltio Horizon',
    stream_data: 'OPC UA and MQTT',
    envelope: '762 x 508 x 508 mm',
    material_ready: 'Inconel 718 wire',
    location: 'Oak Ridge, TN',
    latitude: 35.9606,
    longitude: -84.3048,
  },
  created_at: '2026-06-01T14:04:00Z',
  shared_schema_id: 'SC-8',
  shared_schema_version: '1.0.0',
  message_type: 'response',
  message_subtype: 'assets_offer',
};

const haasFinishAsset = {
  message_uuid: 'demo-asset-haas-finish',
  title: 'HAAS VF-4SS finishing cell',
  body: {
    asset_name: 'HAAS VF-4SS finishing cell',
    asset_source_id: 'asset-haas-vf4ss-02',
    capability_source_id: 'cap-hybrid-ded-cnc',
    machine_builder: 'HAAS',
    controller: 'Haas NGC',
    stream_data: 'MTConnect',
    envelope: '1270 x 508 x 635 mm',
    material_ready: 'Nickel alloys and stainless',
    location: 'Huntsville, AL',
    latitude: 34.7304,
    longitude: -86.5861,
  },
  created_at: '2026-06-01T14:05:00Z',
  shared_schema_id: 'SC-8',
  shared_schema_version: '1.0.0',
  message_type: 'response',
  message_subtype: 'assets_offer',
};

const hybridCapability = {
  message_uuid: 'demo-cap-hybrid-ded-cnc',
  title: 'Hybrid DED build and 5-axis finishing',
  body: {
    capability_name: 'Hybrid DED build and 5-axis finishing',
    capability_source_id: 'cap-hybrid-ded-cnc',
    process: 'Meltio wire-laser deposition with HAAS 5-axis machining',
    material_family: 'Nickel superalloy, stainless steel, titanium',
    tolerance: '+/- 0.05 mm after finish machining',
    available_window: 'Jun 3, 2026 - Jun 7, 2026',
    estimated_lead_time: '5 business days',
    latitude: 35.9606,
    longitude: -84.3048,
  },
  assets: [meltioAsset, haasFinishAsset],
  created_at: '2026-06-01T14:03:00Z',
  shared_schema_id: 'SC-6',
  shared_schema_version: '1.0.0',
  message_type: 'response',
  message_subtype: 'capabilities_offer',
};

const inspectionCapability = {
  message_uuid: 'demo-cap-inspection',
  title: 'Dimensional inspection and first-article report',
  body: {
    capability_name: 'Dimensional inspection and first-article report',
    capability_source_id: 'cap-inspection-fai',
    process: 'CMM inspection with AMX report package',
    tolerance: '+/- 0.025 mm',
    available_window: 'Jun 4, 2026 - Jun 6, 2026',
    estimated_lead_time: '2 business days',
    latitude: 39.9612,
    longitude: -82.9988,
  },
  assets: [
    {
      message_uuid: 'demo-asset-zeiss-cmm',
      title: 'ZEISS CONTURA inspection cell',
      body: {
        asset_name: 'ZEISS CONTURA inspection cell',
        asset_source_id: 'asset-zeiss-contura-03',
        capability_source_id: 'cap-inspection-fai',
        machine_builder: 'ZEISS',
        controller: 'CALYPSO',
        stream_data: 'Inspection report API',
        envelope: '1000 x 1200 x 600 mm',
        location: 'Columbus, OH',
        latitude: 39.9612,
        longitude: -82.9988,
      },
      created_at: '2026-06-01T14:07:00Z',
      shared_schema_id: 'SC-8',
      shared_schema_version: '1.0.0',
      message_type: 'response',
      message_subtype: 'assets_offer',
    },
  ],
  created_at: '2026-06-01T14:06:00Z',
  shared_schema_id: 'SC-6',
  shared_schema_version: '1.0.0',
  message_type: 'response',
  message_subtype: 'capabilities_offer',
};

const offerPartners = [
  {
    offering_partner: amxNetworkPartner,
    time_sent: '2026-06-01T14:07:00Z',
    capabilities_assets_list: [hybridCapability, inspectionCapability],
    standalone_assets: [],
  },
];

const telemetrySeries = [
  { time: 'T+00:00', meltPool: 69, laser: 58, wire: 62, spindle: 24, vibration: 18, gas: 72 },
  { time: 'T+02:00', meltPool: 76, laser: 64, wire: 68, spindle: 30, vibration: 20, gas: 74 },
  { time: 'T+04:00', meltPool: 82, laser: 72, wire: 76, spindle: 36, vibration: 23, gas: 75 },
  { time: 'T+06:00', meltPool: 79, laser: 70, wire: 73, spindle: 42, vibration: 28, gas: 74 },
  { time: 'T+08:00', meltPool: 88, laser: 79, wire: 81, spindle: 51, vibration: 34, gas: 73 },
  { time: 'T+10:00', meltPool: 91, laser: 83, wire: 86, spindle: 55, vibration: 46, gas: 70 },
  { time: 'T+12:00', meltPool: 85, laser: 76, wire: 80, spindle: 61, vibration: 38, gas: 72 },
  { time: 'T+14:00', meltPool: 80, laser: 71, wire: 74, spindle: 67, vibration: 31, gas: 75 },
  { time: 'T+16:00', meltPool: 84, laser: 75, wire: 78, spindle: 63, vibration: 27, gas: 76 },
  { time: 'T+18:00', meltPool: 87, laser: 78, wire: 81, spindle: 59, vibration: 24, gas: 77 },
  { time: 'T+20:00', meltPool: 83, laser: 74, wire: 77, spindle: 54, vibration: 22, gas: 78 },
];

const telemetryChannels = [
  { key: 'meltPool', label: 'Melt pool stability', color: '#2563eb' },
  { key: 'laser', label: 'Laser utilization', color: '#f97316' },
  { key: 'wire', label: 'Wire feed stability', color: '#16a34a' },
  { key: 'spindle', label: 'Spindle load', color: '#7c3aed' },
  { key: 'vibration', label: 'Vibration envelope', color: '#dc2626' },
  { key: 'gas', label: 'Shield gas flow', color: '#0891b2' },
];

const findOfferContext = (messageUuid) => {
  for (const partnerGroup of offerPartners) {
    for (const capability of partnerGroup.capabilities_assets_list) {
      if (capability.message_uuid === messageUuid) {
        return {
          partner: partnerGroup.offering_partner,
          capability,
          asset: capability.assets?.[0] || null,
        };
      }

      const asset = capability.assets?.find((candidate) => candidate.message_uuid === messageUuid);
      if (asset) {
        return {
          partner: partnerGroup.offering_partner,
          capability,
          asset,
        };
      }
    }
  }

  return {
    partner: amxNetworkPartner,
    capability: hybridCapability,
    asset: meltioAsset,
  };
};

const formatDate = (value) => formatDateTime(value);

const buildInitialRequest = () => ({
  shared_schema_id: 'SC-1',
  shared_schema_version: '1.0.0',
  need_category: 'advanced_manufacturing',
  need_category_display_name: 'Advanced Manufacturing',
  need_category_id: 'need-advanced-manufacturing',
  msg_uuid: 'demo-initial-request-msg',
  created_at: '2026-06-01T13:48:00Z',
  request_body: {
    request_title: 'Flight bracket repair coupon',
    part_name: 'Lattice reinforced actuator bracket',
    material: 'Inconel 718',
    quantity: 2,
    target_delivery: '2026-06-07',
    tolerance: '+/- 0.05 mm on machined interfaces',
    requested_outputs: ['Capability match', 'Asset reservation', 'Telemetry-backed build record', 'Expert review'],
  },
});

const buildOffersCard = (selection) => ({
  order: 10,
  title: 'Capability and Asset Selection',
  created_at: '2026-06-01T14:03:00Z',
  updated_at: '2026-06-01T14:07:00Z',
  actions_allowed: !selection,
  selection_made: Boolean(selection),
  selection_expired: false,
  offer_selection_expiration: '2026-06-02T13:40:00Z',
  partners: offerPartners,
});

const buildSelectionCard = (selection) => {
  const selectedItem = selection.type === 'asset'
    ? selection.asset || meltioAsset
    : selection.capability || hybridCapability;
  const selectedBody = selectedItem.body || {};
  const capabilityBody = selection.capability?.body || hybridCapability.body;

  return {
    order: 20,
    title: selection.type === 'asset' ? 'Selected Asset' : 'Selected Capability',
    created_at: '2026-06-01T14:18:00Z',
    updated_at: '2026-06-01T14:18:00Z',
    selection_type: selection.type,
    selected_offer_message_uuid: selectedItem.message_uuid,
    offering_partner_id: selection.partner?.partner_id,
    offering_partner: selection.partner,
    selection_message: {
      message_uuid: 'demo-selection-msg',
      corr_id: PRODUCTION_CORR_ID,
      direction: 'outbound',
      sender_partner: demoWorkspace.created_by_partner,
      shared_schema_id: selection.type === 'asset' ? 'SC-21' : 'SC-17',
      shared_schema_version: '1.0.0',
      message_type: 'request',
      message_subtype: selection.type === 'asset' ? 'select_asset' : 'select_capability',
      created_at: '2026-06-01T14:18:00Z',
      body: {
        selected_offer_message_uuid: selectedItem.message_uuid,
        offering_partner_id: selection.partner?.partner_id,
        offering_partner: selection.partner,
        capability_name: capabilityBody.capability_name,
        capability_source_id: capabilityBody.capability_source_id,
        asset_name: selectedBody.asset_name,
        asset_source_id: selectedBody.asset_source_id,
        selection_reason: 'Best process fit for hybrid deposition, machining, and live telemetry capture.',
      },
      header: {
        schemaId: selection.type === 'asset' ? 'SC-21' : 'SC-17',
        schemaVersion: '1.0.0',
        timestamp: '2026-06-01T14:18:00Z',
      },
    },
  };
};

const buildMachineSelectionCard = (selection) => {
  const asset = selection.asset || meltioAsset;
  const body = asset.body || {};

  return {
    order: 50,
    title: 'Machine Selection and Build Plan',
    created_at: '2026-06-01T14:32:00Z',
    updated_at: '2026-06-01T14:44:00Z',
    instruction: 'AMX has converted the selected offer into a reserved machine path, budget gate, and live process plan.',
    machine: {
      name: body.asset_name || asset.title,
      builder: body.machine_builder || 'HAAS / Meltio',
      controller: body.controller || 'Haas NGC + Meltio Horizon',
      location: body.location || 'Oak Ridge, TN',
      telemetry: body.stream_data || 'OPC UA and MQTT',
    },
    plan: [
      { label: 'Budget gate', status: 'Complete', progress: 100 },
      { label: 'Contract confirmation', status: 'Complete', progress: 100 },
      { label: 'Machine reservation', status: 'Complete', progress: 100 },
      { label: 'Meltio deposition', status: 'Live', progress: 82 },
      { label: 'HAAS finish machining', status: 'Queued', progress: 34 },
    ],
    body: {
      work_order_id: 'WO-DEMO-718-042',
      process_route: 'DED preform, stress relief, 5-axis finish, CMM inspection',
      scheduled_start: '2026-06-03T13:30:00Z',
      expected_release: '2026-06-06T17:00:00Z',
    },
  };
};

const buildTelemetryCard = ({ order = 60, title = 'HAAS/Meltio Process Telemetry', sourceCorrId = PRODUCTION_CORR_ID } = {}) => ({
  order,
  title,
  created_at: '2026-06-01T14:46:00Z',
  updated_at: '2026-06-01T15:12:00Z',
  instruction: 'Live machine signals are normalized for the workspace and kept with the request thread.',
  series: telemetrySeries,
  channels: telemetryChannels,
  metrics: [
    { label: 'Melt pool temp', value: '1268 C', color: '#2563eb' },
    { label: 'Laser power', value: '615 W', color: '#f97316' },
    { label: 'Wire feed', value: '31.8 mm/s', color: '#16a34a' },
    { label: 'Spindle load', value: '59%', color: '#7c3aed' },
  ],
  body: {
    source_thread: sourceCorrId,
    machine: 'HAAS UMC-750 + Meltio Engine Cell',
    ingestion_status: 'MQTT stream accepted into AMX telemetry pipeline',
    event: 'Layer 18 bead height drift corrected after wire feed trim',
    sample_rate: '2 second aggregate',
  },
});

const buildPartModelCard = () => ({
  order: 70,
  title: 'Requested Part Model',
  created_at: '2026-06-01T14:50:00Z',
  updated_at: '2026-06-01T15:03:00Z',
  instruction: 'The requested geometry stays visible beside the build thread so admins can inspect the part context as telemetry arrives.',
  body: {
    model_file: 'actuator-bracket-demo.step',
    material: 'Inconel 718',
    envelope: '142 x 76 x 58 mm',
    mass_estimate: '0.84 kg after finish machining',
    primary_features: 'boss, web ribs, DED repair bead, bolt pattern',
  },
  features: ['DED repair bead', 'Machined boss', 'Ribbed web', 'Inspection datums'],
});

const buildProductionThread = (selection) => {
  const cards = {
    capabilities_assets_offers: buildOffersCard(selection),
  };

  if (selection) {
    const selectedCardKey = selection.type === 'asset' ? 'selected_asset' : 'selected_capability';
    cards[selectedCardKey] = buildSelectionCard(selection);
    cards.partner_confirmation = {
      order: 30,
      title: 'Budget and Contract Confirmation',
      created_at: '2026-06-01T14:21:00Z',
      updated_at: '2026-06-01T14:24:00Z',
      confirmations: [
        {
          message_uuid: 'demo-budget-contract-confirmation',
          corr_id: PRODUCTION_CORR_ID,
          sender_partner: amxNetworkPartner,
          direction: 'inbound',
          shared_schema_id: 'SC-18',
          shared_schema_version: '1.0.0',
          message_type: 'response',
          message_subtype: 'confirm_capability',
          created_at: '2026-06-01T14:24:00Z',
          body: {
            confirmation_status: 'Accepted',
            contract_reference: 'AMX-DEMO-OTA-0042',
            budget_ceiling_usd: 18500,
            funding_status: 'Approved',
            delivery_slot: 'Reserved for Jun 3, 2026',
          },
        },
      ],
    };
    cards.provider_partner_notified = {
      order: 40,
      title: 'Provider Access Opened',
      created_at: '2026-06-01T14:26:00Z',
      updated_at: '2026-06-01T14:26:00Z',
      notifications: [
        {
          message_uuid: 'demo-provider-notified',
          corr_id: PRODUCTION_CORR_ID,
          sender_partner: demoWorkspace.created_by_partner,
          direction: 'outbound',
          shared_schema_id: 'SC-34',
          shared_schema_version: '1.0.0',
          message_type: 'notification',
          message_subtype: 'provider_partner_notified',
          created_at: '2026-06-01T14:26:00Z',
          body: {
            notified_partner: getPartnerDisplayName(amxNetworkPartner),
            access_scope: 'Selected thread plus generated telemetry and build artifacts',
            workspace_access: 'Granted',
          },
        },
      ],
    };
    cards.machine_selection = buildMachineSelectionCard(selection);
    cards.machine_telemetry = buildTelemetryCard();
    cards.part_model = buildPartModelCard();
    cards.files_exchanged = {
      order: 80,
      title: 'Build Artifacts',
      created_at: '2026-06-01T15:08:00Z',
      updated_at: '2026-06-01T15:11:00Z',
      files: [
        {
          message_uuid: 'demo-file-package',
          corr_id: PRODUCTION_CORR_ID,
          sender_partner: amxNetworkPartner,
          direction: 'inbound',
          shared_schema_id: 'SC-12',
          shared_schema_version: '1.0.0',
          message_type: 'file',
          message_subtype: 'generated_artifact',
          created_at: '2026-06-01T15:11:00Z',
          body: {
            file_name: 'amx-demo-build-record.zip',
            contents: 'telemetry CSV, toolpath summary, inspection plan, part thumbnail',
            checksum: 'demo-4f2d7b',
          },
        },
      ],
    };
  }

  return {
    corr_id: PRODUCTION_CORR_ID,
    thread_title: 'Hybrid bracket request and build thread',
    created_at: '2026-06-01T13:48:00Z',
    updated_at: selection ? '2026-06-01T15:18:00Z' : '2026-06-01T14:07:00Z',
    initial_request: buildInitialRequest(),
    thread_cards: cards,
  };
};

const buildExpertThread = () => ({
  corr_id: EXPERT_CORR_ID,
  thread_title: 'Expert support from ingested process data',
  created_at: '2026-06-01T15:13:00Z',
  updated_at: '2026-06-01T15:28:00Z',
  initial_request: {
    shared_schema_id: 'SC-1',
    shared_schema_version: '1.0.0',
    need_category: 'expert_support',
    need_category_display_name: 'Expert Support',
    need_category_id: 'need-expert-support',
    msg_uuid: 'demo-expert-initial-request',
    created_at: '2026-06-01T15:13:00Z',
    instruction: 'This support thread was opened from the machine telemetry captured in the manufacturing thread.',
    request_body: {
      request_title: 'Review Meltio bead-height drift',
      parent_corr_id: PRODUCTION_CORR_ID,
      source_machine: 'HAAS UMC-750 + Meltio Engine Cell',
      trigger_event: 'Layer 18 bead height drift with vibration rise',
      ingested_payloads: ['normalized telemetry', 'toolpath segment', 'build record snapshot'],
    },
  },
  thread_cards: {
    machine_telemetry: buildTelemetryCard({
      order: 10,
      title: 'Ingested Telemetry Snapshot',
      sourceCorrId: PRODUCTION_CORR_ID,
    }),
    expert_recommendations: {
      order: 20,
      title: 'Expert Recommendations',
      created_at: '2026-06-01T15:21:00Z',
      updated_at: '2026-06-01T15:28:00Z',
      expert_name: 'Dr. Maya Chen',
      discipline: 'Hybrid DED process engineering',
      source_corr_id: PRODUCTION_CORR_ID,
      recommendations: [
        {
          title: 'Trim wire feed during thermal recovery',
          detail: 'Reduce wire feed by 4 percent for the next two layers after the heat soak peak, then return to the nominal 31.8 mm/s window.',
        },
        {
          title: 'Hold shield gas at the upper process window',
          detail: 'Keep argon flow at 20 L/min through the rib transition to stabilize the melt pool and reduce oxidation risk.',
        },
        {
          title: 'Delay HAAS finishing until temperature decay is confirmed',
          detail: 'Start finish machining after the part reaches the logged interpass threshold to protect datum stability.',
        },
      ],
      action_items: ['Update Meltio recipe', 'Annotate build record', 'Release inspection hold'],
    },
  },
});

const buildThreads = (selection) => (
  selection
    ? [buildProductionThread(selection), buildExpertThread(selection)]
    : [buildProductionThread(null)]
);

export default function AdminDemoWorkspacePage(props) {
  const [selectedCorrId, setSelectedCorrId] = useState(PRODUCTION_CORR_ID);
  const [selection, setSelection] = useState(null);
  const [actionSuccess, setActionSuccess] = useState('');
  const threads = useMemo(() => buildThreads(selection), [selection]);
  const selectedThread = useMemo(
    () => threads.find((thread) => thread.corr_id === selectedCorrId) || threads[0],
    [threads, selectedCorrId],
  );
  const workspacePartner = demoWorkspace.created_by_partner;
  const workspacePartnerLabel = `${workspacePartner.display_name} (${workspacePartner.partner_id})`;

  useEffect(() => {
    if (!threads.some((thread) => thread.corr_id === selectedCorrId)) {
      setSelectedCorrId(threads[0]?.corr_id || PRODUCTION_CORR_ID);
    }
  }, [threads, selectedCorrId]);

  const commitSelection = async ({ type, item }) => {
    const context = findOfferContext(item.message_uuid);
    const nextSelection = {
      type,
      partner: context.partner,
      capability: context.capability,
      asset: type === 'asset' ? context.asset : context.asset || meltioAsset,
    };
    setSelection(nextSelection);
    setSelectedCorrId(PRODUCTION_CORR_ID);
    setActionSuccess(`${getPartnerDisplayName(context.partner)} selected for ${item.title || item.body?.asset_name || item.body?.capability_name}.`);
  };

  const resetDemo = () => {
    setSelection(null);
    setSelectedCorrId(PRODUCTION_CORR_ID);
    setActionSuccess('');
  };

  const renderThreadsPanel = () => (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Threads
        </Typography>
        <Stack spacing={1}>
          {threads.map((thread) => {
            const selected = selectedThread?.corr_id === thread.corr_id;

            return (
              <ButtonBase
                key={thread.corr_id}
                onClick={() => setSelectedCorrId(thread.corr_id)}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  borderRadius: 2,
                  border: (theme) => `1px solid ${alpha(
                    selected ? theme.palette.primary.main : theme.palette.divider,
                    selected ? 0.62 : 0.78,
                  )}`,
                  bgcolor: (theme) => alpha(
                    selected ? theme.palette.primary.main : theme.palette.background.paper,
                    selected ? 0.065 : 0.6,
                  ),
                  p: 1.25,
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                    {thread.thread_title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    {formatDate(thread.updated_at || thread.created_at)}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Admin Dashboard', href: '/admin' },
              { label: 'Demo Workspace' },
            ]}
          />

          <Stack spacing={3}>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, #083344 0%, #0f766e 44%, #b45309 100%)',
                color: 'common.white',
                boxShadow: (theme) => `0 18px 44px ${alpha(theme.palette.common.black, 0.22)}`,
              }}
            >
              <Stack spacing={1.5} sx={{ maxWidth: 900, minWidth: 0 }}>
                <Typography variant="overline" sx={{ fontWeight: 850, opacity: 0.86 }}>
                  AMX Admin Demo
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                  {demoWorkspace.title}
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.55, opacity: 0.88 }}>
                  {demoWorkspace.description}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<HubRoundedIcon />}
                    label={demoWorkspace.status.toUpperCase()}
                    sx={{ color: 'common.white', borderColor: alpha('#ffffff', 0.36) }}
                    variant="outlined"
                  />
                  <Chip
                    icon={<ShieldMoonRoundedIcon />}
                    label="AMX ADMIN ONLY"
                    sx={{ color: 'common.white', borderColor: alpha('#ffffff', 0.36) }}
                    variant="outlined"
                  />
                  <Chip
                    icon={<ScheduleRoundedIcon />}
                    label={`Updated ${formatDate(demoWorkspace.updated_at)}`}
                    sx={{ color: 'common.white', borderColor: alpha('#ffffff', 0.36) }}
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </Box>

            {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: '340px minmax(0, 1fr)' },
                gap: 3,
                alignItems: 'start',
              }}
            >
              <Stack spacing={2} sx={{ position: { xl: 'sticky' }, top: { xl: 88 } }}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Workspace Signals
                    </Typography>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <ScheduleRoundedIcon color="primary" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Updated
                          </Typography>
                          <Typography variant="body2">{formatDate(demoWorkspace.updated_at)}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <ForumRoundedIcon color="primary" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Partner
                          </Typography>
                          <Typography variant="body2">{workspacePartnerLabel}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <FingerprintRoundedIcon color="primary" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Workspace ID
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {demoWorkspace.workspace_id}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 2.5 }} />

                    <Typography variant="subtitle2" gutterBottom>
                      Workspace Actions
                    </Typography>
                    <Stack spacing={1.5}>
                      <Button
                        variant="outlined"
                        startIcon={<RestartAltRoundedIcon />}
                        onClick={resetDemo}
                        disabled={!selection}
                      >
                        Reset Demo
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        {selection
                          ? 'Selection is active. Telemetry, model, and expert-support threads are visible.'
                          : 'No demo selection has been made yet.'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {renderThreadsPanel()}
              </Stack>

              <Stack spacing={3}>
                <ThreadCardsView
                  key={`${selectedThread.corr_id}-${selection ? 'selected' : 'open'}`}
                  thread={selectedThread}
                  onSelectCapability={(capability) => commitSelection({ type: 'capability', item: capability })}
                  onSelectAsset={(asset) => commitSelection({ type: 'asset', item: asset })}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
